import CommunityFollowButton from '@atoms/CommunityFollowButton';
import Colors from '@constants/Colors';
import { ArrowRight01Icon } from '@hugeicons/core-free-icons';
import { HugeiconsIcon } from '@hugeicons/react-native';
import ProfileSiteButtons from '@molecules/ProfileSiteButtons';
import {
  useBulkGetCommunities,
  useBulkGetProfiles,
  useGetCommunityOrganizers,
  useGetParentCommunityIds,
  useGetTicketsByEventId,
} from '@sunrise-ui/api-client';
import type { Event } from '@sunrise-ui/api/events';
import { ProfileResult } from '@sunrise-ui/composites';
import {
  ProfileImg,
  TextBold,
  TextReg,
  useDynamicStyles,
  type WithResponsive,
} from '@sunrise-ui/primitives';
import { router } from 'expo-router';
import React, { useCallback, useMemo } from 'react';
import { StyleSheet, TouchableOpacity, View } from 'react-native';

const MAX_VISIBLE_ATTENDEES = 5;

type Props = {
  event: Event;
};

/**
 * EventSidebar - Right sidebar for event landing page (desktop)
 *
 * Contains:
 * - Presented By: Parent community with Follow button
 * - Hosted By: Event organizer (event.user_id)
 * - Attending: Avatar stack of ticket holders
 */
export function EventSidebar({ event }: Props) {
  const styles = useDynamicStyles(createStyles);

  // Fetch parent community IDs (the orgs that present this event)
  const { data: parentCommunityIds } = useGetParentCommunityIds({
    communityId: event?.community_id,
  });

  // Fetch all parent communities (presenter orgs) - supports multiple
  const { data: parentCommunities } = useBulkGetCommunities({
    communityIds: parentCommunityIds,
    enabled: Boolean(parentCommunityIds?.length),
  });

  // Only use parent communities - don't fall back to event community
  const presentingCommunities = useMemo(() => {
    if (parentCommunities && parentCommunities.length > 0) {
      return parentCommunities;
    }
    return [];
  }, [parentCommunities]);

  // Get publicly visible organizers for "Hosted By" section
  const { data: organizersMemberships = [] } = useGetCommunityOrganizers(
    event?.community_id,
  );
  const organizerUserIds = useMemo(
    () => organizersMemberships.map((o) => o.user_id),
    [organizersMemberships],
  );

  // Bulk fetch organizer profiles
  const { data: organizerProfiles } = useBulkGetProfiles(organizerUserIds);

  // Fetch attendees (ticket holders)
  const { data: tickets } = useGetTicketsByEventId({
    eventId: event?.id,
    communityId: event?.community_id,
  });

  // Get unique attendee user IDs
  const attendeeUserIds = useMemo(() => {
    if (!tickets) return [];
    const uniqueIds = new Set<string>();
    tickets.forEach((ticket) => {
      if (ticket.user_id && ticket.is_active) {
        uniqueIds.add(ticket.user_id);
      }
    });
    return Array.from(uniqueIds);
  }, [tickets]);

  const visibleAttendeeIds = attendeeUserIds.slice(0, MAX_VISIBLE_ATTENDEES);
  const remainingCount = Math.max(
    0,
    attendeeUserIds.length - MAX_VISIBLE_ATTENDEES,
  );

  // Bulk fetch attendee profiles in a single request
  const { data: attendeeProfiles } = useBulkGetProfiles(visibleAttendeeIds);

  // Create a lookup map for O(1) profile access
  const profilesById = useMemo(() => {
    if (!attendeeProfiles) return new Map();
    return new Map(attendeeProfiles.map((p) => [p.user_id, p]));
  }, [attendeeProfiles]);

  const handleCommunityPress = useCallback((slug?: string) => {
    if (!slug) return;
    router.navigate(`/communities/${slug}`);
  }, []);

  return (
    <View style={styles.container}>
      {/* Presented By Section - Supports multiple parent communities */}
      {presentingCommunities.length > 0 && (
        <View style={styles.section}>
          {presentingCommunities.map((community, index) => (
            <View key={community.id ?? index} style={styles.presentedByRow}>
              <ProfileImg
                userId={community.id ?? ''}
                imgSize="thumbnail_large"
                styleSize={48}
                profile={{
                  first_name: community.name,
                  last_name: '',
                  photos: community.photos,
                }}
              />
              <TouchableOpacity
                style={styles.presentedByInfo}
                onPress={() => handleCommunityPress(community.slug)}
                activeOpacity={0.7}
              >
                {index === 0 && (
                  <TextReg style={styles.presentedByLabel}>
                    Presented by
                  </TextReg>
                )}
                <View style={styles.communityNameRow}>
                  <TextBold style={styles.communityName}>
                    {community.name}
                  </TextBold>
                  <HugeiconsIcon
                    icon={ArrowRight01Icon}
                    size={16}
                    color={Colors.dark.text}
                  />
                </View>
              </TouchableOpacity>
              <CommunityFollowButton communityId={community.id} />
            </View>
          ))}

          {/* Description (using headline from first community) */}
          {presentingCommunities[0]?.headline && (
            <TextReg style={styles.communityDescription} numberOfLines={3}>
              {presentingCommunities[0].headline}
            </TextReg>
          )}

          {/* Social Links from first community */}
          {(presentingCommunities[0]?.links?.linkedin ||
            presentingCommunities[0]?.links?.github ||
            presentingCommunities[0]?.links?.x ||
            presentingCommunities[0]?.links?.website) && (
            <ProfileSiteButtons
              siteLinks={{
                linkedin: presentingCommunities[0].links?.linkedin,
                github: presentingCommunities[0].links?.github,
                x: presentingCommunities[0].links?.x,
                website: presentingCommunities[0].links?.website,
              }}
            />
          )}
        </View>
      )}

      {/* Hosted By Section - Only publicly visible organizers */}
      {organizerProfiles && organizerProfiles.length > 0 && (
        <View style={styles.section}>
          <TextReg style={styles.sectionLabel}>Hosted by</TextReg>

          <View style={styles.hostsContainer}>
            {organizerProfiles.map((organizer) => (
              <ProfileResult
                key={organizer.user_id}
                userId={organizer.user_id}
                profile={organizer}
                shouldNavigateOnClick
                size="small"
                isOneLineText
              />
            ))}
          </View>
        </View>
      )}

      {/* Attending Section - only show when there are real attendees */}
      {visibleAttendeeIds.length > 0 && (
        <View style={styles.section}>
          <TextReg style={styles.sectionLabel}>
            {attendeeUserIds.length} Attending
          </TextReg>

          <View style={styles.attendeesContainer}>
            <View style={styles.avatarStack}>
              {visibleAttendeeIds.map((userId, index) => {
                const profile = profilesById.get(userId);
                return (
                  <View
                    key={userId}
                    style={[
                      styles.avatarWrapper,
                      { marginLeft: index > 0 ? -12 : 0 },
                    ]}
                  >
                    <ProfileImg
                      userId={userId}
                      imgSize="thumbnail_small"
                      styleSize={32}
                      profile={profile}
                      style={styles.attendeeAvatar}
                    />
                  </View>
                );
              })}
            </View>
            {remainingCount > 0 && (
              <TextReg style={styles.attendeeCount}>
                +{remainingCount} others
              </TextReg>
            )}
          </View>
        </View>
      )}
    </View>
  );
}

const createStyles = ({ isMobile }: WithResponsive) =>
  StyleSheet.create({
    container: {
      width: '100%',
      gap: isMobile ? 20 : 32,
    },
    section: {
      gap: 16,
    },
    // Presented By - inline layout
    presentedByRow: {
      flexDirection: 'row',
      alignItems: 'center',
      gap: 12,
    },
    presentedByInfo: {
      flex: 1,
    },
    presentedByLabel: {
      fontSize: 12,
      color: Colors.dark.text,
      opacity: 0.6,
    },
    communityNameRow: {
      flexDirection: 'row',
      alignItems: 'center',
      gap: 4,
    },
    communityName: {
      fontSize: 16,
      fontWeight: '600',
      color: Colors.dark.text,
    },
    communityDescription: {
      fontSize: 14,
      color: Colors.dark.text,
      opacity: 0.8,
      lineHeight: 20,
    },
    sectionLabel: {
      fontSize: 12,
      color: Colors.dark.text,
      opacity: 0.6,
      textTransform: 'uppercase',
      letterSpacing: 0.5,
    },
    hostsContainer: {
      gap: 12,
    },
    attendeesContainer: {
      flexDirection: 'row',
      alignItems: 'center',
      gap: 12,
    },
    avatarStack: {
      flexDirection: 'row',
      alignItems: 'center',
    },
    attendeeCount: {
      fontSize: 13,
      color: Colors.dark.text,
      opacity: 0.7,
    },
    avatarWrapper: {
      borderWidth: 2,
      borderColor: Colors.dark.purple0,
      borderRadius: 16,
    },
    attendeeAvatar: {
      borderRadius: 16,
    },
  });
